/**
 * useMusterFit - which density step the muster draws at (utils/musterFit), by MEASURING its list.
 *
 * After each render the list's content is compared with its room, and the step climbs one while it overflows: the
 * climb happens in layout effects, before the browser paints, so a crowded column never shows its overflowing
 * frame. When the column's STRUCTURE changes (`key`: someone joined, left, folded, acted - `engine/roster/muster`
 * `fitKey`) it climbs on from where it is at once, and starts again from step 1 only once the structure has held
 * still for `MUSTER_SETTLE_MS` (a departure may fit at a lighter step). A resized window starts over at once. A wound
 * changes neither, so a fight's damage never re-fits it.
 *
 * Why not start over at once: a burst of arrivals (80 added, reaching this screen one delivery at a time) restarted
 * and climbed back three steps for each, and React counted those climbs as nested updates - past fifty, it threw and
 * the actor rail crashed (the fuzzer, 2026-11-01). A burst is now one re-fit, after it.
 *
 * The column's height restarts the climb; the list's own height only re-measures (climbing, never back): step 3
 * shrinks the rail above the list, which grows the list, and a restart on that would drop back and oscillate.
 *
 * @param {{ listRef: {current: HTMLElement|null}, columnRef: {current: HTMLElement|null}, key: string,
 *   max: import('../utils/musterFit').MusterStep }} args
 * @returns {import('../utils/musterFit').MusterStep}
 */
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { stepAfter } from '../utils/musterFit';

/** How long the structure must hold still before the fit starts over from full chips (ms). */
export const MUSTER_SETTLE_MS = 300;

/** Calls `onChange` whenever `ref`'s element changes height. */
function useHeightWatch(ref, onChange) {
  useEffect(() => {
    const el = ref.current;
    const Observer = globalThis.ResizeObserver;
    if (!el || !Observer) return undefined;
    let height = el.clientHeight;
    const ro = new Observer(() => {
      if (el.clientHeight === height) return;
      height = el.clientHeight;
      onChange();
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [ref, onChange]);
}

/** Calls `restart` once `key` has held still for `MUSTER_SETTLE_MS` after a change (the mount fits the first). */
function useSettledRestart(key, restart) {
  const first = useRef(true);
  useEffect(() => {
    if (first.current) {
      first.current = false;
      return undefined;
    }
    const t = setTimeout(restart, MUSTER_SETTLE_MS);
    return () => clearTimeout(t);
  }, [key, restart]);
}

export default function useMusterFit({ listRef, columnRef, key, max }) {
  // `pass` counts measuring passes asked for from outside a render: a restart that lands on the step it was already
  // at must still re-measure (the review: at step 1, a smaller window changed no dependency and nothing was read).
  const [fit, setFit] = useState({ step: 1, pass: 0 });
  const { step } = fit;
  useLayoutEffect(() => {
    const el = listRef.current;
    if (!el) return;
    const next = stepAfter(step, { content: el.scrollHeight, room: el.clientHeight }, max);
    if (next !== step) setFit((f) => ({ ...f, step: next }));
    // Each climb re-renders with the new step, which runs this again: the climb ends where the list fits.
  }, [listRef, step, key, max, fit.pass]);
  const [verbs] = useState(() => ({
    // The column's height changed (a resized window), or a new structure held still: start over from full chips.
    restart: () => setFit((f) => ({ ...f, step: 1, pass: f.pass + 1 })),
    // The list's own height changed (the rail above it grew a line): measure again, and only ever climb - the
    // slim rail at step 3 grows the list too, and a restart on that would undo the step that caused it.
    remeasure: () => setFit((f) => ({ ...f, pass: f.pass + 1 })),
  }));
  useHeightWatch(columnRef, verbs.restart);
  useHeightWatch(listRef, verbs.remeasure);
  useSettledRestart(key, verbs.restart);
  return step;
}
